import * as React from "react";
import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy } from "lucide-react";
import * as Sentry from "@sentry/react";
import type { Message } from "@/components/feature/layout/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { extractCitation, removeMarkdown } from "../helpers";
import { removeCitations } from "@/views/chat/helpers";

interface Props {
  message: Message;
}

export const AssistantMessageCopy: React.FC<Props> = ({ message }) => {
  const [copiedMessage, setCopiedMessage] = useState<boolean>(false);
  const { rootItem } = useCurrentPdf();

  const copyMessage = async (stripCitations: boolean): Promise<void> => {
    if (!rootItem) {
      return;
    }

    try {
      const cleanedText = removeMarkdown(
        stripCitations ? removeCitations(message.content) : extractCitation(message, rootItem),
      );

      await navigator.clipboard.writeText(cleanedText);
      setCopiedMessage(true);
      setTimeout(() => setCopiedMessage(false), 2000);
    } catch (error) {
      console.warn(error);
      Sentry.captureException(error);
    }
  };

  const handleCopyWithCitations = () => {
    void copyMessage(false);
  };

  const handleCopyWithoutCitations = () => {
    void copyMessage(true);
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-2 flex items-center self-end rounded-md bg-white px-2 py-1 text-xs text-gray-600 shadow-sm transition-colors hover:bg-gray-100"
          aria-label="Copy message"
        >
          {copiedMessage ? (
            <>
              <Check size={12} className="mr-1 text-green-500" />
              Copied
            </>
          ) : (
            <>
              <Copy size={12} className="mr-1" />
              Copy
            </>
          )}
        </motion.button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleCopyWithCitations} className="cursor-pointer">
          With citations
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopyWithoutCitations} className="cursor-pointer">
          Without citations
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
